import {
  SessionStateEnum,
  SocketMessageDtoV2,
  SocketMessageDtoV2EventTypeEnum,
  SocketMessagePayloadV2,
  SocketMessagePayloadV2StateEnum,
} from "../orchestrator-api-client";
import setSessionState from "../store/actions/set-session-state.action";
import setClosedSession from "../store/actions/set-closed-session.action.ts";
import {
  selectCurrentState,
  selectLoan,
} from "../store/selectors/select.slice.ts";
import { pushErrorMonitor } from "../features/analytics/analytics.service.ts";
import { LayoutError } from "../store/slices/layout/types/layout-error.ts";
import { AppStore } from "../store/store.ts";
import {
  setFirstSession,
  setIsWebsocketConnected,
  setKeepAlive,
  setLastAck,
} from "../store/slices/session/session.slice.ts";
import { setError, setLoading } from "../store/slices/layout/layout.slice.ts";
import { debug, error } from "../utilities/logger.ts";
import setClosedWebSocket from "../store/actions/set-closed-websocket.action.ts";
import { CoralogixLogSeverity, CoralogixRum } from "@coralogix/browser";
import { clearPongTimeout } from "./websocket-keep-alive.ts";

const isAckValid = (ack: string | undefined, lastAck: string | undefined) => {
  return ack === lastAck;
};

export const onClose = (store: AppStore) => (event: CloseEvent) => {
  debug(`[WS] Closed. (${event})`, event.type);
  CoralogixRum.log(
    CoralogixLogSeverity.Info,
    `[WS] Closed. (${event})`,
    event.type,
  );
  clearPongTimeout();
  store.dispatch(setIsWebsocketConnected(false));
  store.dispatch(setFirstSession(true));
};

export const onError = (store: AppStore) => (event: Event) => {
  const details =
    event instanceof ErrorEvent
      ? { message: event.message, error: event.error }
      : { type: event.type };
  error("[WS] Error", details);
  CoralogixRum.log(CoralogixLogSeverity.Error, "[WS] Error", details);
  clearPongTimeout();
  store.dispatch(setClosedWebSocket());
  // pushErrorMonitor(event.type); // send error monitor when websocket failed
};

export const onMessage =
  (store: AppStore, ws: WebSocket) => (event: MessageEvent) => {
    debug("[WS]: message received", event.data);
    CoralogixRum.log(
      CoralogixLogSeverity.Info,
      "[WS]: message received",
      event.data,
    );
    const message = JSON.parse(event.data) as SocketMessageDtoV2;
    const state = store.getState();
    let currentState: SessionStateEnum = selectCurrentState(store.getState());
    const { eventType, payload } = message;

    if (typeof payload === "undefined") {
      error("[WS]: payload undefined");
      CoralogixRum.log(CoralogixLogSeverity.Error, "[WS]: payload undefined");
      return;
    }

    if (
      eventType === SocketMessageDtoV2EventTypeEnum.GoToNextState ||
      eventType === SocketMessageDtoV2EventTypeEnum.Connect
    ) {
      const state: SocketMessagePayloadV2StateEnum | undefined = payload.state;
      const busy: boolean | undefined = payload.busy;

      if (typeof state === "undefined") {
        error("[WS]: state undefined");
        CoralogixRum.log(CoralogixLogSeverity.Error, "[WS]: state undefined");
        return;
      }

      if (
        eventType === SocketMessageDtoV2EventTypeEnum.GoToNextState &&
        currentState !== SessionStateEnum.KycInProgress //TODO: temporary remove ack check on KYC_IN_PROGRESS page, verify if there is a way to persist lastkAck)
      ) {
        //check for ack validity only on go to next state ws messages because of api call that contains ack
        const { ack } = message;
        const lastAck = store.getState().session.lastAck;
        if (isAckValid(ack, lastAck)) {
          // check if ack received via ws is the same as last api response
          if (state && !busy) {
            store.dispatch(setSessionState(state as SessionStateEnum));
            store.dispatch(setLastAck(undefined));
            return;
          } else if (busy && state) {
            store.dispatch(setLoading(true));
            return;
          }
        } else {
          error(
            `ack: ${ack} and lastAck ${lastAck} did not match; ignored message ${message}`,
          );
          CoralogixRum.log(
            CoralogixLogSeverity.Info,
            `ack: ${ack} and lastAck ${lastAck} diversi; messaggio ignorato ${message}`,
          );
        }
      } else {
        // check if ack received via ws is the same as last api response
        if (state && !busy) {
          store.dispatch(setSessionState(state as SessionStateEnum));
          return;
        } else if (busy && state) {
          store.dispatch(setLoading(true));
          return;
        }
      }
    }

    if (eventType === SocketMessageDtoV2EventTypeEnum.Disconnect) {
      debug(`[WS]: closing connection`);
      CoralogixRum.log(CoralogixLogSeverity.Info, "[WS]: closing connection");
      store.dispatch(setClosedSession()); // will trigger the popup multi session
      store.dispatch(setKeepAlive(false));

      return;
    }

    if (eventType === SocketMessageDtoV2EventTypeEnum.Pong) {
      clearPongTimeout();
      return;
    }

    if (eventType === SocketMessageDtoV2EventTypeEnum.Error) {
      const { workflowId, dealId } = selectLoan(store.getState());
      const { channel } = selectLoan(state);
      const { bodyResponse = "{}" } = payload as SocketMessagePayloadV2;

      let errorStr = "";
      try {
        errorStr = JSON.stringify(JSON.parse(bodyResponse), null, 2);
      } catch (e: any) {
        error("Decode 'bodyResponse' error", e);
        CoralogixRum.log(
          CoralogixLogSeverity.Error,
          "Decode 'bodyResponse' error",
          e,
        );
        pushErrorMonitor(e); // send error monitor when websocket failed
      }

      ws.send(
        JSON.stringify({
          eventType: SocketMessageDtoV2EventTypeEnum.Metric,
          payload: {
            metric: {
              name: SocketMessageDtoV2EventTypeEnum.Error,
              value: 1,
              unit: "COUNT",
              dimensions: {
                pageName: payload.state,
                channel: channel || "DIRECT",
              },
            },
          },
        }),
      );

      const err: LayoutError = {
        title: "Errore di elaborazione",
        description: "Ti invitiamo a contattare il supporto.",
        error: [
          "OR Error:",
          `workflow id: ${workflowId ?? "{undefined}"}`,
          `deal id: ${dealId ?? "{undefined}"}`,
          `Body response: ${errorStr}`,
        ].join("\n"),
      };
      store.dispatch(setError(err));
    }
  };

export const onOpen = (store: AppStore) => (event: Event) => {
  debug("Connessione aperta", event.type);
  CoralogixRum.log(
    CoralogixLogSeverity.Info,
    `[WS] Open. (${event})`,
    event.type,
  );
  store.dispatch(setIsWebsocketConnected(true));
  store.dispatch(setKeepAlive(true));
};
